import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import clsx from 'clsx'
import { Project } from 'Models/project'
import React, { useEffect } from 'react'
import { availableProjectParamters } from 'shared/utils/projectParameterUtils'
import ProjectGoogleMap from './ProjectGoogleMap'

const useStyles = makeStyles((theme: Theme) => ({
    firstHeaderText: {
        marginTop: 4,
    },
    headerMarginTop: {
        marginTop: theme.spacing(1.5),
    },
    headerText: {
        color: theme.palette.secondary.main,
        borderBottom: '4px solid #fff',
        display: 'inline-block',
        fontWeight: 'bold',
        fontSize: pxToRem(13),
        marginBottom: theme.spacing(0.75),
        textTransform: 'uppercase',
    },
    contentText: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: pxToRem(48),
        color: '#fff',
    },
    container: {
        width: '100%',
        fontWeight: 400,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    paper: {
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
        marginBottom: theme.spacing(1),
    },
    dividerRigth: {
        [theme.breakpoints.up('lg')]: {
            borderRight: '1px solid rgba(255, 255, 255, 0.25);',
        },
    },
    itemHead: {
        paddingBottom: theme.spacing(0.75),
    },
    item: {
        fontWeight: 500,
        paddingBottom: theme.spacing(0.75),
        '& .tooltipEllipsis': {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
        },
    },
    addressPadding: {
        paddingBottom: theme.spacing(2.5),
    },
    paddingLeft: {
        '&.MuiGrid-item': {
            paddingRight: 0,
        },

        [theme.breakpoints.up('lg')]: {
            '&.MuiGrid-item': {
                paddingLeft: theme.spacing(3),
            },
        },
    },
    paddingRight: {
        [theme.breakpoints.up('md')]: {
            paddingRight: theme.spacing(1),
        },
    },
    tooltip: {
        border: 0,
    },
    detailsLeft: {
        [theme.breakpoints.up('lg')]: {
            overflow: 'auto',
            maxHeight: 325,
        },
    },
}))

interface ProjectDetailsProps {
    projectData: Partial<Project> | undefined
}

export const ProjectDetails: React.FC<ProjectDetailsProps> = ({
    projectData,
}) => {
    const theme = useTheme()
    const classes = useStyles()
    const [addressTextWidth, setAddressTextWidth] = React.useState<
        number | undefined
    >(undefined)

    const smDown = useMediaQuery(theme.breakpoints.down('sm'))
    const lgDown = useMediaQuery(theme.breakpoints.down('lg'))

    const projectDate = React.useMemo(() => {
        return projectData?.projectDate?.split('T')[0]
    }, [projectData])

    const issueDate = React.useMemo(() => {
        return projectData?.issueDate?.split('T')[0]
    }, [projectData])

    const getStreet = () => {
        const street: (string | undefined)[] = [
            projectData?.location?.street1,
            projectData?.location?.street2,
        ].filter((value) => !!value && value !== 'N/A')

        return `${street.join(', ')}, ${projectData?.location?.city}`
    }

    const getParameterValue = (parameterName: string) => {
        const parameter = projectData?.parameters?.find(
            (parameter) => parameter?.modifierList?.name === parameterName
        )

        if ((parameter?.selectedModifiers || []).length > 0) {
            return parameter!
                .selectedModifiers!.map((item) => item!.name)
                .join(', ')
        } else if (
            parameterName === 'Owner Type' &&
            !((parameter?.selectedModifiers || []).length > 0)
        ) {
            return projectData?.owner
        }

        return ''
    }

    useEffect(() => {
        if (smDown) {
            setAddressTextWidth(713)
            return
        }
        if (lgDown) {
            setAddressTextWidth(970)
            return
        }

        setAddressTextWidth(undefined)
    }, [lgDown, smDown])

    return (
        <GridContainer
            data-testid="dvProjectDetailsContainer"
            alignItems="stretch"
            justifyContent="flex-start"
            className={classes.container}
        >
            <GridItem
                xs={12}
                sm={12}
                md={12}
                lg={6}
                xl={6}
                className={classes.detailsLeft}
                tabIndex={0}
            >
                <GridContainer
                    className={clsx(classes.paddingRight, classes.dividerRigth)}
                >
                    <GridItem xs={12}>
                        <Typography
                            className={clsx(
                                classes.firstHeaderText,
                                classes.headerText
                            )}
                            variant="button"
                            display="block"
                            gutterBottom
                        >
                            General Information
                        </Typography>
                    </GridItem>
                    <GridItem xs={12}>
                        <GridContainer>
                            <GridItem className={classes.itemHead} xs={6}>
                                Project Group:
                            </GridItem>
                            <GridItem
                                xs={6}
                                className={classes.item}
                                data-testid="dvProjectGroup"
                            >
                                <TooltipEllipsis text={projectData?.groupName}>
                                    {projectData?.groupName}
                                </TooltipEllipsis>
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem className={classes.itemHead} xs={6}>
                                Project Name:
                            </GridItem>
                            <GridItem
                                xs={6}
                                className={classes.item}
                                data-testid="dvProjectName"
                            >
                                <TooltipEllipsis text={projectData?.name}>
                                    {projectData?.name}
                                </TooltipEllipsis>
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem className={classes.itemHead} xs={6}>
                                Project Number:
                            </GridItem>
                            <GridItem
                                xs={6}
                                className={classes.item}
                                data-testid="dvProjectNumber"
                            >
                                <TooltipEllipsis text={projectData?.number}>
                                    {projectData?.number}
                                </TooltipEllipsis>
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem className={classes.itemHead} xs={6}>
                                Construction Budget:
                            </GridItem>
                            <GridItem
                                xs={6}
                                className={classes.item}
                                data-testid="dvProjectBudget"
                            >
                                <TooltipEllipsis text={projectData?.budget}>
                                    {projectData?.budget}
                                </TooltipEllipsis>
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem className={classes.itemHead} xs={6}>
                                Gross Building Area:
                            </GridItem>
                            <GridItem
                                xs={6}
                                className={classes.item}
                                data-testid="dvProjectBuildingArea"
                            >
                                <TooltipEllipsis
                                    text={projectData?.grossBuildingArea}
                                >
                                    {projectData?.grossBuildingArea}
                                </TooltipEllipsis>
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem className={classes.itemHead} xs={6}>
                                Project Date:
                            </GridItem>
                            <GridItem
                                xs={6}
                                className={classes.item}
                                data-testid="dvProjectDate"
                            >
                                <TooltipEllipsis text={projectDate}>
                                    {projectDate}
                                </TooltipEllipsis>
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem className={classes.itemHead} xs={6}>
                                Issue For:
                            </GridItem>
                            <GridItem
                                xs={6}
                                className={classes.item}
                                data-testid="dvIssueFor"
                            >
                                <TooltipEllipsis text={projectData?.issueFor}>
                                    {projectData?.issueFor}
                                </TooltipEllipsis>
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem className={classes.itemHead} xs={6}>
                                Issue Date:
                            </GridItem>
                            <GridItem
                                xs={6}
                                className={classes.item}
                                data-testid="dvIssueDate"
                            >
                                <TooltipEllipsis text={issueDate}>
                                    {issueDate}
                                </TooltipEllipsis>
                            </GridItem>
                        </GridContainer>
                        <GridContainer>
                            <GridItem className={classes.itemHead} xs={6}>
                                Addendum Number:
                            </GridItem>
                            <GridItem
                                xs={6}
                                className={classes.item}
                                data-testid="dvAddendumNumber"
                            >
                                <TooltipEllipsis
                                    text={projectData?.addendumNumber}
                                >
                                    {projectData?.addendumNumber}
                                </TooltipEllipsis>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem xs={12}>
                        <Typography
                            className={clsx(
                                classes.headerText,
                                classes.headerMarginTop
                            )}
                            variant="button"
                            display="block"
                            gutterBottom
                        >
                            Project Parameters
                        </Typography>
                    </GridItem>
                    <GridItem xs={12} data-testid="dvProjectParamters">
                        {availableProjectParamters.map((param, index) => (
                            <GridContainer key={index}>
                                <GridItem className={classes.itemHead} xs={6}>
                                    {param.label}:
                                </GridItem>
                                <GridItem
                                    xs={6}
                                    className={classes.item}
                                    aria-label="parameterRole"
                                >
                                    <TooltipEllipsis
                                        text={getParameterValue(param.name)}
                                    >
                                        {getParameterValue(param.name)}
                                    </TooltipEllipsis>
                                </GridItem>
                            </GridContainer>
                        ))}
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem
                className={clsx(classes.paddingLeft)}
                xs={12}
                sm={12}
                md={12}
                lg={6}
                xl={6}
            >
                <GridContainer>
                    <GridItem xs={12}>
                        <Typography
                            className={clsx(
                                classes.firstHeaderText,
                                classes.headerText
                            )}
                            variant="button"
                            display="block"
                            gutterBottom
                        >
                            Project Address
                        </Typography>
                    </GridItem>
                    <GridItem
                        xs={12}
                        className={clsx('projectStreet', classes.item)}
                        data-testid="dvProjectStreet1"
                    >
                        <TooltipEllipsis
                            text={getStreet()}
                            className={classes.tooltip}
                            width={addressTextWidth}
                        >
                            {getStreet()}
                        </TooltipEllipsis>
                    </GridItem>
                    <GridItem
                        xs={12}
                        className={clsx(classes.item, classes.addressPadding)}
                        data-testid="dvProjectAddress"
                    >
                        <TooltipEllipsis
                            text={`${projectData?.location?.region}, ${projectData?.location?.postalCode}, ${projectData?.location?.country} `}
                        >
                            {`${projectData?.location?.region}, ${projectData?.location?.postalCode}, ${projectData?.location?.country} `}
                        </TooltipEllipsis>
                    </GridItem>

                    <GridItem xs={12}>
                        <ProjectGoogleMap projectData={projectData} />
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    )
}
export default ProjectDetails
